/**
 * Temporary diagnostics for the unfocusable deduction search input (prod-only race).
 *
 * Enable: localStorage.setItem("DEBUG_SEARCH_FOCUS", "1") then reload.
 * Manual dump when stuck: window.__dumpSearchFocus()
 * Attempt repair: window.__repairSearchFocus()
 */

const DEBUG_KEY = "DEBUG_SEARCH_FOCUS";

type AncestorReport = {
  tag: string;
  id: string;
  className: string;
  inert: boolean;
  ariaHidden: string | null;
  pointerEvents: string;
  tabIndex: string | null;
};

type SearchFocusDump = {
  at: string;
  activeElement: string;
  input: {
    exists: boolean;
    disabled: boolean;
    readOnly: boolean;
    tabIndex: number | null;
    isConnected: boolean;
  };
  ancestors: AncestorReport[];
  inertNodes: string[];
  ariaHiddenNodes: string[];
  elementsAtClick: string[];
  didFocusAfterClick: boolean | null;
};

const describeEl = (el: Element | null): string => {
  if (!el) return "null";
  const id = el.id ? `#${el.id}` : "";
  const cls = typeof el.className === "string" && el.className ? `.${el.className.trim().split(/\s+/).slice(0, 3).join(".")}` : "";
  return `${el.tagName.toLowerCase()}${id}${cls}`;
};

const collectAncestors = (el: Element | null): AncestorReport[] => {
  const ancestors: AncestorReport[] = [];
  let current: Element | null = el;
  while (current) {
    const html = current as HTMLElement;
    ancestors.push({
      tag: current.tagName.toLowerCase(),
      id: current.id,
      className: typeof current.className === "string" ? current.className : "",
      inert: Boolean(html.inert),
      ariaHidden: current.getAttribute("aria-hidden"),
      pointerEvents: getComputedStyle(current).pointerEvents,
      tabIndex: current.getAttribute("tabindex"),
    });
    current = current.parentElement;
  }
  return ancestors;
};

export const dumpSearchFocus = (clickX?: number, clickY?: number): SearchFocusDump => {
  const input = document.getElementById("deduction-search") as HTMLInputElement | null;
  const elementsAtClick =
    typeof clickX === "number" && typeof clickY === "number"
      ? document.elementsFromPoint(clickX, clickY).slice(0, 8).map(describeEl)
      : [];

  const dump: SearchFocusDump = {
    at: new Date().toISOString(),
    activeElement: describeEl(document.activeElement),
    input: {
      exists: Boolean(input),
      disabled: Boolean(input?.disabled),
      readOnly: Boolean(input?.readOnly),
      tabIndex: input?.tabIndex ?? null,
      isConnected: Boolean(input?.isConnected),
    },
    ancestors: collectAncestors(input),
    inertNodes: [...document.querySelectorAll("[inert]")].map(describeEl),
    ariaHiddenNodes: [...document.querySelectorAll('[aria-hidden="true"]')]
      .filter(el => !(el as HTMLElement).closest(".drawer-overlay, [data-react-aria-top-layer]"))
      .slice(0, 30)
      .map(describeEl),
    elementsAtClick,
    didFocusAfterClick: null,
  };

  // eslint-disable-next-line no-console
  console.warn("[DEBUG_SEARCH_FOCUS]", dump);
  return dump;
};

/**
 * Clears leftover React Aria `inert` / modal `aria-hidden` after the drawer is gone.
 * Safe only when no modal/drawer overlay is currently open.
 */
export const repairSearchFocusBlockers = (): { repairedInert: number; repairedAriaHidden: number } => {
  const openOverlay = document.querySelector(
    '.drawer-overlay, [aria-modal="true"], [data-react-aria-top-layer]'
  );
  if (openOverlay) {
    return { repairedInert: 0, repairedAriaHidden: 0 };
  }

  let repairedInert = 0;
  let repairedAriaHidden = 0;

  document.querySelectorAll("[inert]").forEach(node => {
    if (!(node instanceof HTMLElement)) return;
    node.inert = false;
    repairedInert += 1;
  });

  // React Aria hides body siblings with aria-hidden when inert is unsupported.
  // Only clear direct body children that look like app shells, not intentional UI.
  [...document.body.children].forEach(child => {
    if (!(child instanceof HTMLElement)) return;
    if (child.getAttribute("aria-hidden") !== "true") return;
    if (child.hasAttribute("data-react-aria-top-layer")) return;
    if (child.classList.contains("drawer-overlay")) return;
    child.removeAttribute("aria-hidden");
    repairedAriaHidden += 1;
  });

  if (repairedInert > 0 || repairedAriaHidden > 0) {
    // eslint-disable-next-line no-console
    console.warn("[DEBUG_SEARCH_FOCUS] repaired focus blockers", { repairedInert, repairedAriaHidden });
  }

  return { repairedInert, repairedAriaHidden };
};

export const isSearchFocusDebugEnabled = (): boolean => {
  try {
    return globalThis.localStorage?.getItem(DEBUG_KEY) === "1";
  } catch {
    return false;
  }
};

export const installSearchFocusDebugGlobals = (): void => {
  const win = globalThis as typeof globalThis & {
    __dumpSearchFocus?: typeof dumpSearchFocus;
    __repairSearchFocus?: typeof repairSearchFocusBlockers;
  };
  win.__dumpSearchFocus = dumpSearchFocus;
  win.__repairSearchFocus = repairSearchFocusBlockers;
};

export const attachSearchFocusClickProbe = (container: HTMLElement): (() => void) => {
  const onPointerDown = (event: PointerEvent) => {
    const { clientX, clientY } = event;
    const before = dumpSearchFocus(clientX, clientY);

    requestAnimationFrame(() => {
      const input = document.getElementById("deduction-search");
      const focused = document.activeElement === input;
      // eslint-disable-next-line no-console
      console.warn("[DEBUG_SEARCH_FOCUS] after click", {
        didFocus: focused,
        activeElement: describeEl(document.activeElement),
        blockingAncestor: before.ancestors.find(a => a.inert || a.ariaHidden === "true" || a.pointerEvents === "none"),
      });

      if (!focused) {
        repairSearchFocusBlockers();
        input?.focus();
        // eslint-disable-next-line no-console
        console.warn("[DEBUG_SEARCH_FOCUS] repair+refocus attempted", {
          activeElement: describeEl(document.activeElement),
        });
      }
    });
  };

  container.addEventListener("pointerdown", onPointerDown, true);
  return () => container.removeEventListener("pointerdown", onPointerDown, true);
};
